import type { AdoptionData } from "@/hooks/useAdoption";
import type { MetricsData } from "@/hooks/useMetrics";
import type { TokenCostItem } from "@/hooks/useTokenCost";
import type { ExecKpiCard, ExecKpiFamily, OrgGovernanceRollup } from "@/types/executive";
import { blendedRates, toDailyCosts, projectEndOfMonth } from "@/lib/cost/burnRate";

type Translate = (key: string, opts?: Record<string, unknown>) => string;

// ---------------------------------------------------------------------------
// Source constants
// ---------------------------------------------------------------------------

const ADOPTION_SOURCE = {
  hook: "useAdoption",
  endpoint: "GET /api/v2/adoption",
} as const;

const METRICS_SOURCE = {
  hook: "useMetrics",
  endpoint: "GET /api/v2/metrics",
} as const;

const GOVERNANCE_SOURCE = {
  hook: "useOrgGovernancePulse",
  endpoint: "GET /api/v2/governance/rollup",
} as const;

const COST_SOURCE = {
  hook: "useTokenCost",
  endpoint: "GET /api/v2/cost/tokens",
} as const;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function avail(value: unknown, note?: string): ExecKpiCard["availability"] {
  return { available: value !== null, ...(note ? { note } : {}) };
}

function scoreToTrend(score: number | null): ExecKpiCard["trend"] {
  if (score === null) return "neutral";
  if (score >= 70) return "good";
  if (score >= 40) return "warning";
  return "bad";
}

function pendingToTrend(pending: number): ExecKpiCard["trend"] {
  if (pending > 500) return "bad";
  if (pending > 100) return "warning";
  return "good";
}

function percentCard(
  id: string,
  label: string,
  value: number | null,
  note: string | undefined,
): ExecKpiCard {
  return {
    id,
    label,
    value,
    unit: "%",
    delta: null,
    trend: "neutral",
    isProxy: false,
    source: { ...ADOPTION_SOURCE },
    availability: avail(value, note),
  };
}

function nullCard(
  id: string,
  label: string,
  unit: string,
  source: ExecKpiCard["source"],
  isProxy = false,
): ExecKpiCard {
  return {
    id,
    label,
    value: null,
    unit,
    delta: null,
    trend: "neutral",
    isProxy,
    source,
    availability: avail(null),
  };
}

// ---------------------------------------------------------------------------
// adaptAdoption (S1 canonical — maps useAdoption to ExecKpiFamily)
// ---------------------------------------------------------------------------

export function adaptAdoption(
  data: AdoptionData | null,
  t: Translate,
): ExecKpiFamily {
  const activeRatio =
    data && data.total_members > 0
      ? Math.round((data.active_members / data.total_members) * 100)
      : null;

  const rate = data?.quality.first_pass_gate_success_rate ?? null;
  const gateRate =
    data && data.quality.sample_size > 0 && rate !== null
      ? Math.round(rate * 100)
      : null;

  const windowNote = data
    ? t("executive.adoption.windowNote", { days: data.period_days })
    : undefined;

  return {
    id: "adoption",
    label: t("executive.adoption.title"),
    cards: [
      percentCard(
        "active_ratio",
        t("executive.adoption.activeRatio"),
        activeRatio,
        activeRatio === null ? t("executive.adoption.noData") : windowNote,
      ),
      percentCard(
        "gate_success_rate",
        t("executive.adoption.gateSuccessRate"),
        gateRate,
        gateRate === null
          ? t(data ? "executive.adoption.noGateData" : "executive.adoption.noData")
          : windowNote,
      ),
    ],
    planGated: false,
    adminOnly: false,
    periodFilterExclusion: t("executive.adoption.periodExclusion"),
    drilldown: { route: "", labelKey: "executive.drilldown.adoption" },
  };
}

// ---------------------------------------------------------------------------
// toProductivityFamily (adapted from S2 to S1 schema)
// ---------------------------------------------------------------------------

/** Backend KPI ids are snake_case; i18n keys under executive.kpi are camelCase. */
const KPI_I18N_KEY: Record<string, string> = {
  cycle_time: "cycleTime",
  deploy_frequency: "deployFrequency",
  change_failure_rate: "changeFailureRate",
  rework_rate: "reworkRate",
};

export function toProductivityFamily(
  metrics: MetricsData | null,
  t: Translate,
): ExecKpiFamily {
  if (!metrics || !metrics.has_data || metrics.kpis.length === 0) {
    return {
      id: "productivity",
      label: t("executive.family.productivity"),
      cards: [
        nullCard("cycle_time", t("executive.kpi.cycleTime"), "días", METRICS_SOURCE),
        nullCard("deploy_frequency", t("executive.kpi.deployFrequency"), "/sem", METRICS_SOURCE, true),
        nullCard("change_failure_rate", t("executive.kpi.changeFailureRate"), "%", METRICS_SOURCE, true),
        nullCard("rework_rate", t("executive.kpi.reworkRate"), "%", METRICS_SOURCE),
      ],
      planGated: false,
      adminOnly: false,
      drilldown: { route: "", labelKey: "executive.seeDetail" },
    };
  }

  const cards: ExecKpiCard[] = metrics.kpis.map((k) => ({
    id: k.id,
    label: t(`executive.kpi.${KPI_I18N_KEY[k.id] ?? k.id}`),
    value: k.value,
    unit: k.unit,
    delta: k.delta,
    trend: k.trend === "warning" ? "warning" : k.trend,
    isProxy: k.is_proxy ?? false,
    source: METRICS_SOURCE,
    availability: avail(k.value),
  }));

  return {
    id: "productivity",
    label: t("executive.family.productivity"),
    cards,
    planGated: false,
    adminOnly: false,
    drilldown: { route: "", labelKey: "executive.seeDetail" },
  };
}

// ---------------------------------------------------------------------------
// toGovernanceFamily (adapted from S2 to S1 schema)
// ---------------------------------------------------------------------------

export function toGovernanceFamily(
  rollup: OrgGovernanceRollup | null,
  t: Translate,
): ExecKpiFamily {
  if (!rollup) {
    return {
      id: "governance",
      label: t("executive.family.governance"),
      cards: [
        nullCard("gov_score", t("executive.kpi.govScore"), "", GOVERNANCE_SOURCE),
        nullCard("gate_pass_rate", t("executive.kpi.gatePassRate"), "%", GOVERNANCE_SOURCE),
        nullCard("pending_gates", t("executive.kpi.pendingGates"), "", GOVERNANCE_SOURCE),
      ],
      planGated: false,
      adminOnly: false,
      drilldown: { route: "", labelKey: "executive.seeDetail" },
    };
  }

  const gatePct =
    rollup.pass_count + rollup.fail_count > 0
      ? Math.round((rollup.pass_count / (rollup.pass_count + rollup.fail_count)) * 100)
      : null;

  return {
    id: "governance",
    label: t("executive.family.governance"),
    cards: [
      {
        id: "gov_score",
        label: t("executive.kpi.govScore"),
        value: rollup.score !== null ? Math.round(rollup.score) : null,
        unit: "",
        delta: null,
        trend: scoreToTrend(rollup.score),
        isProxy: false,
        source: GOVERNANCE_SOURCE,
        availability: avail(rollup.score),
      },
      {
        id: "gate_pass_rate",
        label: t("executive.kpi.gatePassRate"),
        value: gatePct,
        unit: "%",
        delta: null,
        trend: scoreToTrend(gatePct),
        isProxy: false,
        source: GOVERNANCE_SOURCE,
        availability: avail(gatePct),
      },
      {
        id: "pending_gates",
        label: t("executive.kpi.pendingGates"),
        value: rollup.pending_count,
        unit: "",
        delta: null,
        trend: pendingToTrend(rollup.pending_count),
        isProxy: false,
        source: GOVERNANCE_SOURCE,
        availability: avail(rollup.pending_count),
      },
    ],
    planGated: false,
    adminOnly: false,
    drilldown: { route: "", labelKey: "executive.seeDetail" },
  };
}

// ---------------------------------------------------------------------------
// toCostFamily (adapted from S2 to S1 schema)
// ---------------------------------------------------------------------------

export function toCostFamily(
  items: TokenCostItem[],
  isAdmin: boolean,
  todayIso: string,
  t: Translate,
): ExecKpiFamily {
  const noDataFamily: ExecKpiFamily = {
    id: "cost",
    label: t("executive.family.cost"),
    cards: [
      nullCard("mtd", t("executive.kpi.mtd"), "USD", COST_SOURCE),
      nullCard("projected_eom", t("executive.kpi.projectedEom"), "USD", COST_SOURCE),
      nullCard("avg_daily_7d", t("executive.kpi.avgDaily7d"), "USD/día", COST_SOURCE),
    ],
    planGated: true,
    adminOnly: true,
    drilldown: { route: "cost", labelKey: "executive.seeDetail" },
  };

  if (!isAdmin || items.length === 0) return noDataFamily;

  const modelItems = items.filter((i) => i.model !== null);
  const dateItems = items.filter((i) => i.date !== null);
  const rates = blendedRates(modelItems);
  if (!rates) return noDataFamily;

  const dailyCosts = toDailyCosts(dateItems, rates);
  const projection = projectEndOfMonth(dailyCosts, todayIso);
  if (!projection) return noDataFamily;

  const { mtd, avgDaily7, projectedEom } = projection;

  return {
    id: "cost",
    label: t("executive.family.cost"),
    cards: [
      { id: "mtd", label: t("executive.kpi.mtd"), value: mtd, unit: "USD", delta: null, trend: "neutral", isProxy: false, source: COST_SOURCE, availability: avail(mtd) },
      { id: "projected_eom", label: t("executive.kpi.projectedEom"), value: projectedEom, unit: "USD", delta: null, trend: "neutral", isProxy: false, source: COST_SOURCE, availability: avail(projectedEom) },
      { id: "avg_daily_7d", label: t("executive.kpi.avgDaily7d"), value: avgDaily7, unit: "USD/día", delta: null, trend: "neutral", isProxy: false, source: COST_SOURCE, availability: avail(avgDaily7) },
    ],
    planGated: true,
    adminOnly: true,
    drilldown: { route: "cost", labelKey: "executive.seeDetail" },
  };
}
